import { generateObjectId } from '@alpod-ai-writer/shared';
import type { CursorData } from '@alpod-ai-writer/shared';
import { Button, TextInput } from 'flowbite-react';
import { ChevronDownIcon, PlusIcon, TrashIcon } from 'lucide-react';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';

import { TextEditor } from './textEditor.component';

export interface AttachmentsEditorProps {
    yDoc: Y.Doc;
    contentId: string;
    cursorData: CursorData;
    toolbarEl?: HTMLDivElement|null;
    readOnly?: boolean;
}

export function useAttachmentItems(yDoc: Y.Doc, contentId: string) {
    const arr = yDoc.getArray<Y.Map<string>>('att_items_' + contentId);

    const [itemIds, setItemIds] = useState<string[]>(() =>
        arr.toArray().map((m) => m.get('id') ?? '')
    );

    useEffect(() => {
        const handler = () => setItemIds(arr.toArray().map((m) => m.get('id') ?? ''));
        arr.observe(handler);
        return () => arr.unobserve(handler);
    }, [arr]);

    const addItem = useCallback(() => {
        const id = generateObjectId();
        const map = new Y.Map<string>();
        map.set('id', id);
        arr.push([map]);
    }, [arr]);

    const removeItem = useCallback((id: string) => {
        const index = arr.toArray().findIndex((m) => m.get('id') === id);
        if (index !== -1) arr.delete(index, 1);
    }, [arr]);

    return { itemIds, addItem, removeItem };
}

export const AttachmentsAddButton: React.FC<{ yDoc: Y.Doc; contentId: string }> = ({ yDoc, contentId }) => {
    const { addItem } = useAttachmentItems(yDoc, contentId);
    return (
        <Button color="primary" size="sm" onClick={addItem}>
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Pridať prílohu
        </Button>
    );
};

function AttachmentItemTitle({
    yDoc,
    itemId,
    readOnly,
}: {
    yDoc: Y.Doc;
    itemId: string;
    readOnly: boolean;
}) {
    const yText = yDoc.getText('att_title_' + itemId);
    const [value, setValue] = useState(() => yText.toString());

    useEffect(() => {
        const handler = () => setValue(yText.toString());
        yText.observe(handler);
        return () => yText.unobserve(handler);
    }, [yText]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        yDoc.transact(() => {
            yText.delete(0, yText.length);
            yText.insert(0, newVal);
        });
    }, [yDoc, yText]);

    return (
        <TextInput
            value={value}
            onChange={handleChange}
            placeholder="Název přílohy"
            disabled={readOnly}
            sizing="sm"
            className="font-medium"
        />
    );
}

function AttachmentItemTitleDisplay({ yDoc, itemId }: { yDoc: Y.Doc; itemId: string }) {
    const yText = yDoc.getText('att_title_' + itemId);
    const [value, setValue] = useState(() => yText.toString());

    useEffect(() => {
        const handler = () => setValue(yText.toString());
        yText.observe(handler);
        return () => yText.unobserve(handler);
    }, [yText]);

    return (
        <span className={clsx('text-base font-medium', value ? 'text-gray-800' : 'text-gray-400 italic')}>
            {value || 'Bez názvu'}
        </span>
    );
}

export const AttachmentsEditor: React.FC<AttachmentsEditorProps> = ({
    yDoc,
    contentId,
    cursorData,
    toolbarEl,
    readOnly = false,
}) => {
    const { itemIds, removeItem } = useAttachmentItems(yDoc, contentId);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const prevLengthRef = useRef(itemIds.length);

    // Auto-expand newly added item
    useEffect(() => {
        if (itemIds.length > prevLengthRef.current) {
            setExpandedId(itemIds[itemIds.length - 1]);
        }
        prevLengthRef.current = itemIds.length;
    }, [itemIds]);

    if (itemIds.length === 0) {
        return (
            <p className="px-4 md:px-8 text-sm text-gray-500 italic">
                {readOnly ? 'Žiadne prílohy.' : 'Žiadne prílohy. Pridajte prvú pomocou tlačidla vyššie.'}
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-3 px-4 md:px-8">
            {itemIds.map((id, idx) => {
                const isOpen = expandedId === id;
                return (
                    <div
                        key={id}
                        className={clsx(
                            'flex flex-col rounded-xl overflow-hidden border-2 bg-white transition-all duration-200',
                            isOpen ? 'border-primary-400 shadow-sm shadow-primary-100' : 'border-gray-200'
                        )}
                    >
                        {/* Header — always visible, click to toggle */}
                        <div
                            className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-gray-50 transition-colors"
                            onClick={() => setExpandedId(isOpen ? null : id)}
                        >
                            <div className="flex flex-col grow min-w-0">
                                <span className="text-xs text-gray-400 font-mono mb-0.5">Príloha {idx + 1}</span>
                                <AttachmentItemTitleDisplay yDoc={yDoc} itemId={id} />
                            </div>
                            {!readOnly && (
                                <button
                                    className="p-1.5 text-gray-300 hover:text-red-500 rounded-md transition-colors shrink-0"
                                    onClick={(e) => { e.stopPropagation(); removeItem(id); }}
                                    title="Odstrániť prílohu"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            )}
                            <ChevronDownIcon
                                className={clsx(
                                    'h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200',
                                    isOpen && 'rotate-180'
                                )}
                            />
                        </div>

                        {/* Body — visible only when expanded */}
                        {isOpen && (
                            <div className="flex flex-col gap-3 px-4 pb-4 border-t border-gray-100">
                                <div className="pt-3">
                                    <AttachmentItemTitle yDoc={yDoc} itemId={id} readOnly={readOnly} />
                                </div>
                                <TextEditor
                                    documentField={'att_body_' + id}
                                    documentFieldList={[]}
                                    cursorData={cursorData}
                                    yjsNode={yDoc.get('att_body_' + id, Y.XmlText)}
                                    className="relative w-full bg-gray-50 rounded-md p-3 min-h-[120px]"
                                    toolbarEl={toolbarEl}
                                    overrideReadOnly={readOnly}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
